export const daysOfTheWeek = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

interface DayRange {
  startDay: number
  endDay: number
}
interface HourRange {
  openHour: number
  openMin: number
  closeHour: number
  closeMin: number
  deltas: { min: number; max: number }
}
interface Range {
  days: DayRange
  hours: HourRange
  deltas: { min: number; max: number }
}
export interface HoursByDay {
  Sunday: HourRange
  Monday: HourRange
  Tuesday: HourRange
  Wednesday: HourRange
  Thursday: HourRange
  Friday: HourRange
  Saturday: HourRange
}
type Holiday = 'Thanksgiving' | 'Christmas' | undefined
export interface HourDetails {
  days: HoursByDay
  ranges: Range[]
  isOpen: boolean
  openCloseDate: Date | null
  holiday?: Holiday
}

const twentyFourHourRange: Range = {
  days: { startDay: 1, endDay: 0 },
  hours: {
    openHour: 0,
    openMin: 0,
    closeHour: 23,
    closeMin: 59,
    deltas: { min: 0, max: 10079 },
  },
  deltas: { min: 0, max: 10079 },
}

const getDayOfWeek = (subStr: string) =>
  daysOfTheWeek.findIndex((d) =>
    d.toLowerCase().startsWith(subStr.toLowerCase().trim())
  )

const buildDayRange = (days: string) => {
  const [startDay, endDay] = days.split('-')
  return {
    startDay: getDayOfWeek(startDay),
    endDay: getDayOfWeek(endDay || startDay),
  }
}

const convertTime = (raw: string) => {
  if (!raw) return [0, 0]
  raw = raw.toLowerCase()
  const adjust = ~raw.indexOf('pm') ? 12 : 0
  const [hour, min] = raw.split(':')
  return [
    Number((hour ?? '0').replace(/[^0-9]+/g, '')) + adjust,
    Number((min ?? '0').replace(/[^0-9]+/g, '')),
  ]
}

const buildHourRange = (hours: string) => {
  const [open, close] = hours.split('-')
  const [openHour, openMin] = convertTime(open)
  const [closeHour, closeMin] = convertTime(close)
  return {
    openHour,
    openMin,
    closeHour,
    closeMin,
  }
}

const calculateDelta = (day: number, hour: number, min: number): number =>
  day * 24 * 60 + hour * 60 + min

const calculateDateDelta = (date: Date): number =>
  calculateDelta(date.getDay(), date.getHours(), date.getMinutes())

// Holidays are currently disabled until they can be handled for the proper countries
//
//
// const getThanksgivingDay = (year?: number) => {
//   year = year ?? new Date().getFullYear()
//   const lastOfNov = new Date(year, 10, 30).getDay()
//   return new Date(year, 10, (lastOfNov > 5 ? 34 : 27) - lastOfNov)
// }
//
// const isHoliday = (date?: Date): Holiday => {
//   date = date || new Date()
//   return date == getThanksgivingDay()
//     ? 'Thanksgiving'
//     : date.getMonth() == 11 && date.getDate() == 25
//     ? 'Christmas'
//     : undefined
// }
const calculateRangeDeltas = (range: Range): { min: number; max: number } => ({
  min: calculateDelta(
    range.days.startDay,
    range.hours.openHour,
    range.hours.openMin
  ),
  max: calculateDelta(
    range.days.endDay,
    range.hours.closeHour,
    range.hours.closeMin
  ),
})
const buildRanges = (hours: string): Range[] => {
  if (~hours.indexOf('24')) return [twentyFourHourRange]
  const ranges = hours.split(';')

  if (!ranges.length || !~ranges[0].indexOf(':')) return []
  return ranges
    .map((part) => {
      const [days, hours] = part.split(':')
      const range = {
        days: buildDayRange(days),
        hours: buildHourRange(hours),
      } as Range
      range.deltas = calculateRangeDeltas(range)
      return range
    })
    .sort((a, b) => a.days.startDay - b.days.startDay)
}

const nextOpenDate = (
  date: Date,
  days: HoursByDay
): { isOpen: boolean; openCloseDate: Date | null; holiday?: Holiday } => {
  const day = date.getDay()
  const dow = daysOfTheWeek[day] as keyof HoursByDay
  const delta = calculateDateDelta(date)
  let isOpen, openCloseDelta
  if (
    days[dow] &&
    days[dow].deltas.min <= delta &&
    days[dow].deltas.max >= delta
  ) {
    isOpen = true
    openCloseDelta = days[dow].deltas.max
  } else if (days[dow] && days[dow].deltas.min > delta) {
    isOpen = false
    openCloseDelta = days[dow].deltas.min
  } else {
    isOpen = false
    for (let i = 1; i < 7; i++) {
      const nextDay = daysOfTheWeek[
        day + i > 6 ? day + i - 6 : day + i
      ] as keyof HoursByDay
      if (days[nextDay]) {
        openCloseDelta = days[nextDay].deltas.min
        break
      }
    }
  }

  const openCloseDate = openCloseDelta
    ? new Date(date.getTime() + (openCloseDelta - delta) * 60 * 1000)
    : null

  return { isOpen, openCloseDate }
}

export const createHourString = (hour: number, min: number) => {
  const isPM = hour > 12
  return `${isPM ? hour - 12 : hour}${min ? ':' + min : ''}${
    isPM ? 'pm' : 'am'
  }`
}

const buildHoursByDay = (ranges: Range[]) => {
  return daysOfTheWeek.reduce((res, day, i) => {
    const range = ranges?.find(
      (r) =>
        (r.days.startDay || 7) <= (i || 7) && (r.days.endDay || 7) >= (i || 7)
    )
    if (range)
      res[day as keyof HoursByDay] = {
        ...range.hours,
        deltas: {
          min: calculateDelta(i, range.hours.openHour, range.hours.openMin),
          max: calculateDelta(i, range.hours.closeHour, range.hours.closeMin),
        },
      }
    return res
  }, {} as HoursByDay)
}
export const parseStoreHours = (hours: string): HourDetails | null => {
  const ranges = buildRanges(hours)
  if (!ranges.length) return null
  const days = buildHoursByDay(ranges)
  return { days, ranges, ...nextOpenDate(new Date(), days) }
}
